import { Component, OnInit, signal, computed, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PursesService } from '../../../api-client/api/purses.service';
import { TournamentsService } from '../../../api-client/api/tournaments.service';
import { LoggingService } from '../../../services/shared/logging/logging.service';
import { ConfigService } from '../../../services/config/config.service';
import { ToastrService } from 'ngx-toastr';
import { ModalService } from '../../../services/shared/modal/modal.service';
import { PurseAssignmentConfirmationDialogComponent, PurseAssignmentSummary } from './purse-assignment-confirmation-dialog.component';
import { AuditInfoComponent } from '../../shared/audit-info/audit-info.component';
import { finalize, catchError, of, map } from 'rxjs';
import { ModulesPursesApplicationPurseBreakdownsSavePlayerPurseAssignmentsPlayerPurseAssignmentDto } from '../../../api-client/model/modulesPursesApplicationPurseBreakdownsSavePlayerPurseAssignmentsPlayerPurseAssignmentDto';
import { ModulesPursesApplicationPurseBreakdownsSavePlayerPurseAssignmentsSavePlayerPurseAssignmentsRequest } from '../../../api-client/model/modulesPursesApplicationPurseBreakdownsSavePlayerPurseAssignmentsSavePlayerPurseAssignmentsRequest';
import { ModulesPursesApplicationPurseBreakdownsImportPurseBreakdownFromXmlImportPurseBreakdownFromXmlResponse } from '../../../api-client/model/modulesPursesApplicationPurseBreakdownsImportPurseBreakdownFromXmlImportPurseBreakdownFromXmlResponse';

interface PurseSettings {
    totalPurse: number;
    adminFee: number;
    firstPlacePercentage: number;
    placesToPay: number;
}

interface PurseBreakdown {
    tournamentPlayerId: number;
    playerId: number;
    position: string;
    status: 'PRO' | 'AM';
    playerName: string;
    percentage: number;
    projected: number;
    assigned: number;
    isEditing: boolean;
    isDirty: boolean;
    validationError: string | null;
}

interface PurseBreakdownResponse {
    purseBreakdownId?: string;
    tournamentId?: string;
    totalPurse?: number;
    adminFeePercentage?: number;
    createdOnUtc?: Date;
    modifiedOnUtc?: Date;
}

interface PurseProjectionItem {
    tournamentPlayerId: number;
    playerId: number;
    firstName: string;
    lastName: string;
    position: number;
    positionTied: boolean;
    isAmateur: boolean;
    projectedPayout: number;
    percentage: number;
    assignedPayout?: number;
}

@Component({
    selector: 'app-purse',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, AuditInfoComponent],
    templateUrl: './purse.component.html',
    styleUrl: './purse.component.scss'
})
export class PurseComponent implements OnInit {
    private readonly loggingService = inject(LoggingService);
    private readonly route = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);
    private readonly pursesService = inject(PursesService);
    private readonly tournamentsService = inject(TournamentsService);
    private readonly configService = inject(ConfigService);
    private readonly toastr = inject(ToastrService);
    private readonly modalService = inject(ModalService);

    private get apiBasePath(): string {
        return this.configService.config?.apiBasePath || '';
    }

    // Loading states
    isLoading = signal<boolean>(false);
    isSaving = signal<boolean>(false);
    isImporting = signal<boolean>(false);
    isLoadingProjection = signal<boolean>(false);

    // Tournament context
    tournamentId = signal<string | null>(null);
    tournamentName = signal<string>('Tournament');
    purseBreakdownId = signal<string | null>(null);

    // Purse settings
    purseSettings = signal<PurseSettings>({
        totalPurse: 2000000,
        adminFee: 6.0,
        firstPlacePercentage: 15.0,
        placesToPay: 141
    });

    // Import file path
    importFilePath = signal<string>('');
    private selectedFile: File | null = null;

    // Purse breakdown data (loaded from API on init)
    purseBreakdowns = signal<PurseBreakdown[]>([]);
    purseCreatedOnUtc = signal<Date | null>(null);
    purseModifiedOnUtc = signal<Date | null>(null);

    // Pagination
    currentPage = signal<number>(1);
    pageSize = 10;

    // Computed properties
    netPurse = computed(() => {
        const settings = this.purseSettings();
        return settings.totalPurse * (1 - settings.adminFee / 100);
    });

    firstPlacePrize = computed(() => {
        const settings = this.purseSettings();
        return settings.totalPurse * (settings.firstPlacePercentage / 100);
    });

    totalProjected = computed(() => {
        return this.purseBreakdowns().reduce((sum, item) => sum + item.projected, 0);
    });

    totalAssigned = computed(() => {
        return this.purseBreakdowns().reduce((sum, item) => sum + item.assigned, 0);
    });

    variance = computed(() => {
        return this.totalAssigned() - this.totalProjected();
    });

    purseTotalsWarning = computed(() => {
        const net = this.netPurse();
        if (net <= 0) {
            return null;
        }

        const totalAssigned = this.totalAssigned();
        const epsilon = 0.01;

        if (totalAssigned - net > epsilon) {
            const over = totalAssigned - net;
            return `Assigned payouts exceed the available purse by ${this.formatCurrencyDetailed(over)}.`;
        }

        const shortfall = net - totalAssigned;
        if (shortfall > net * 0.1) {
            return `Assigned payouts are ${this.formatCurrencyDetailed(shortfall)} below the available purse.`;
        }

        return null;
    });

    paginatedBreakdowns = computed(() => {
        const breakdowns = this.purseBreakdowns();
        const start = (this.currentPage() - 1) * this.pageSize;
        const end = start + this.pageSize;
        return breakdowns.slice(start, end);
    });

    totalPages = computed(() => {
        return Math.ceil(this.purseBreakdowns().length / this.pageSize);
    });

    hasUnsavedChanges = computed(() => {
        return this.purseBreakdowns().some(b => b.isDirty);
    });

    hasValidationErrors = computed(() => {
        return this.purseBreakdowns().some(b => b.validationError !== null);
    });

    dirtyCount = computed(() => {
        return this.purseBreakdowns().filter(b => b.isDirty).length;
    });

    ngOnInit(): void {
        this.route.paramMap
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(params => {
                const id = params.get('tournamentId');
                if (id) {
                    this.tournamentId.set(id);
                    this.loadPurseData(id);
                }
            });
    }

    private loadPurseData(tournamentId: string): void {
        this.loggingService.debug('PurseComponent', 'Loading purse data for tournament:', tournamentId);
        this.isLoading.set(true);

        // First, fetch tournament to get current purse amount
        this.tournamentsService.getTournamentByIdEndpoint(tournamentId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError(error => {
                    this.loggingService.error('PurseComponent', 'Error loading tournament:', error);
                    this.toastr.error('Failed to load tournament data', 'Error');
                    return of(null);
                })
            )
            .subscribe(tournament => {
                if (!tournament) {
                    this.isLoading.set(false);
                    return;
                }

                const tournamentPurse = tournament.purse || 0;
                this.tournamentName.set(tournament.name || 'Tournament');

                // Then fetch purse breakdown
                this.pursesService.getPurseBreakdownByTournamentIdEndpoint(tournamentId)
                    .pipe(
                        takeUntilDestroyed(this.destroyRef),
                        catchError(error => {
                            this.loggingService.error('PurseComponent', 'Error loading purse data:', error);
                            if (error.status === 404) {
                                this.loggingService.info('PurseComponent', 'No purse breakdown found for tournament');
                                return of(null);
                            }
                            this.toastr.error('Failed to load purse data', 'Error');
                            return of(null);
                        }),
                        finalize(() => this.isLoading.set(false))
                    )
                    .subscribe(data => {
                        if (data) {
                            this.purseBreakdownId.set(data.purseBreakdownId || null);
                            this.purseCreatedOnUtc.set(data.createdOnUtc ? new Date(data.createdOnUtc) : null);
                            this.purseModifiedOnUtc.set(data.modifiedOnUtc ? new Date(data.modifiedOnUtc) : null);
                            const breakdownPurse = data.totalPurse || 0;

                            // Legacy behavior: If breakdown purse doesn't match tournament purse, sync them
                            if (breakdownPurse !== tournamentPurse) {
                                this.loggingService.info('PurseComponent',
                                    `Syncing purse breakdown (${breakdownPurse}) with tournament purse (${tournamentPurse})`);
                                this.syncPurseWithTournament(data.purseBreakdownId!, tournamentPurse, data.adminFeePercentage || 0);
                            }

                            this.purseSettings.set({
                                totalPurse: tournamentPurse, // Always use tournament purse
                                adminFee: (data.adminFeePercentage || 0) * 100,
                                firstPlacePercentage: this.purseSettings().firstPlacePercentage,
                                placesToPay: this.purseSettings().placesToPay
                            });
                            this.loggingService.info('PurseComponent', 'Purse data loaded successfully');

                            // Automatically load projection after purse settings are loaded
                            this.loadPurseProjection(tournamentId);
                        } else {
                            this.loggingService.info('PurseComponent', 'No existing purse breakdown, ready to create');
                            this.purseCreatedOnUtc.set(null);
                            this.purseModifiedOnUtc.set(null);

                            // Set purse from tournament
                            this.purseSettings.update(settings => ({
                                ...settings,
                                totalPurse: tournamentPurse
                            }));

                            // Still load projection even without existing purse breakdown
                            this.loadPurseProjection(tournamentId);
                        }
                    });
            });
    }

    onBrowseFile(): void {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.xml';
        input.onchange = (event: Event) => {
            const target = event.target as HTMLInputElement;
            const file = target.files?.[0];
            if (file) {
                this.selectedFile = file;
                this.importFilePath.set(file.name);
                this.loggingService.debug('PurseComponent', 'File selected:', file.name);
            }
        };
        input.click();
    }

    onImportFile(): void {
        if (!this.selectedFile) {
            this.toastr.error('Please select an XML file first', 'Validation Error');
            return;
        }

        const tournamentId = this.tournamentId();
        if (!tournamentId) {
            this.toastr.error('Tournament ID is required', 'Validation Error');
            return;
        }

        this.isImporting.set(true);
        this.loggingService.info('PurseComponent', 'Importing purse breakdown from:', this.selectedFile.name);

        const reader = new FileReader();
        reader.onload = (e) => {
            const xmlContent = e.target?.result as string;

            if (!this.validateXmlContent(xmlContent)) {
                this.isImporting.set(false);
                return;
            }

            this.pursesService.tournamentsIdPurseBreakdownImportPost(tournamentId, xmlContent)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    catchError(error => {
                        this.loggingService.error('PurseComponent', 'Error importing XML:', error);
                        this.toastr.error(
                            error?.error?.message || 'Failed to import purse breakdown from XML',
                            'Import Error'
                        );
                        return of(null);
                    }),
                    finalize(() => this.isImporting.set(false))
                )
                .subscribe(result => {
                    if (!result) {
                        return;
                    }

                    const validation = result.validation;
                    if (validation && validation.isValid === false) {
                        const errorMessage = validation.errors && validation.errors.length > 0
                            ? validation.errors.join('; ')
                            : 'Purse breakdown import failed validation.';
                        this.toastr.error(errorMessage, 'Import Validation Failed');
                        return;
                    }

                    this.toastr.success('Purse breakdown imported successfully', 'Success');
                    this.selectedFile = null;
                    this.importFilePath.set('');
                    // Reload the data
                    this.loadPurseData(tournamentId);
                });
        };

        reader.onerror = () => {
            this.isImporting.set(false);
            this.toastr.error('Failed to read file', 'File Error');
            this.loggingService.error('PurseComponent', 'Error reading file');
        };

        reader.readAsText(this.selectedFile);
    }

    private validateXmlContent(xmlContent: string): boolean {
        try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');

            // Check for parsing errors
            const parserError = xmlDoc.querySelector('parsererror');
            if (parserError) {
                this.toastr.error('Invalid XML format', 'Validation Error');
                return false;
            }

            // Check for required root element
            if (xmlDoc.documentElement.tagName !== 'PurseBreakdown') {
                this.toastr.error('XML must have PurseBreakdown as root element', 'Validation Error');
                return false;
            }

            // Validate required fields
            const totalPurse = xmlDoc.querySelector('TotalPurse');
            const adminFee = xmlDoc.querySelector('AdminFeePercentage');

            if (!totalPurse || !adminFee) {
                this.toastr.error('XML must contain TotalPurse and AdminFeePercentage elements', 'Validation Error');
                return false;
            }

            const positionsElement = xmlDoc.querySelector('Positions');
            if (!positionsElement) {
                this.toastr.error('XML must contain a Positions element with at least one Position entry', 'Validation Error');
                return false;
            }

            const positionNodes = Array.from(positionsElement.querySelectorAll('Position'));
            if (positionNodes.length === 0) {
                this.toastr.error('XML must contain a Positions element with at least one Position entry', 'Validation Error');
                return false;
            }

            const positionNumbers: number[] = [];
            let totalPercentage = 0;

            for (const positionNode of positionNodes) {
                const numberAttr = positionNode.getAttribute('number');
                const percentageAttr = positionNode.getAttribute('percentage');

                if (!numberAttr || !percentageAttr) {
                    this.toastr.error('Each Position must include number and percentage attributes', 'Validation Error');
                    return false;
                }

                const positionNumber = parseInt(numberAttr, 10);
                if (!Number.isInteger(positionNumber) || positionNumber <= 0) {
                    this.toastr.error('Position numbers must be positive integers', 'Validation Error');
                    return false;
                }

                const percentage = parseFloat(percentageAttr);
                if (Number.isNaN(percentage) || percentage < 0 || percentage > 1) {
                    this.toastr.error('Position percentages must be between 0 and 1', 'Validation Error');
                    return false;
                }

                positionNumbers.push(positionNumber);
                totalPercentage += percentage;
            }

            const epsilon = 0.000001;
            if (totalPercentage - 1 > epsilon) {
                this.toastr.error('Position percentages must sum to 1.0 or less', 'Validation Error');
                return false;
            }

            const sortedNumbers = [...positionNumbers].sort((a, b) => a - b);
            for (let i = 0; i < sortedNumbers.length; i++) {
                if (sortedNumbers[i] !== i + 1) {
                    this.toastr.error('Position numbers must be sequential starting at 1 without gaps', 'Validation Error');
                    return false;
                }
            }

            return true;
        } catch (error) {
            this.loggingService.error('PurseComponent', 'XML validation error:', error);
            this.toastr.error('Failed to validate XML content', 'Validation Error');
            return false;
        }
    }

    private loadPurseProjection(tournamentId: string): void {
        this.isLoadingProjection.set(true);
        this.loggingService.info('PurseComponent', 'Loading purse projection for tournament:', tournamentId);

        this.pursesService.calculatePurseProjectionEndpoint(tournamentId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                map(response =>
                    (response ?? []).map(item => ({
                        tournamentPlayerId: item.tournamentPlayerId ?? 0,
                        playerId: item.playerId ?? 0,
                        firstName: item.firstName ?? '',
                        lastName: item.lastName ?? '',
                        position: item.position ?? 0,
                        positionTied: item.positionTied ?? false,
                        isAmateur: item.isAmateur ?? false,
                        projectedPayout: item.projectedPayout ?? 0,
                        percentage: item.percentage ?? 0,
                        assignedPayout: item.assignedPayout ?? undefined
                    } satisfies PurseProjectionItem))
                ),
                catchError(error => {
                    this.loggingService.error('PurseComponent', 'Error loading projection:', error);
                    if (error.status !== 404) {
                        this.toastr.error(error?.error?.message || 'Failed to load purse projection', 'Error');
                    }
                    return of<PurseProjectionItem[]>([]);
                }),
                finalize(() => this.isLoadingProjection.set(false))
            )
            .subscribe(projections => {
                this.processPurseProjections(projections);
            });
    }

    private processPurseProjections(projections: PurseProjectionItem[]): void {
        if (projections && projections.length > 0) {
            // Map API response to purse breakdown grid
            const updatedBreakdowns = projections.map(proj => {
                const positionDisplay = proj.positionTied ? `T${proj.position}` : `${proj.position}`;
                const status: 'PRO' | 'AM' = proj.isAmateur ? 'AM' : 'PRO';
                const assignedPayout = proj.assignedPayout ?? proj.projectedPayout;

                return {
                    tournamentPlayerId: proj.tournamentPlayerId,
                    playerId: proj.playerId,
                    position: positionDisplay,
                    status: status,
                    playerName: `${proj.lastName}, ${proj.firstName}`,
                    percentage: proj.percentage * 100, // Convert decimal to percentage
                    projected: proj.projectedPayout,
                    assigned: assignedPayout,
                    isEditing: false,
                    isDirty: false,
                    validationError: null
                };
            });

            this.purseBreakdowns.set(updatedBreakdowns);
            this.currentPage.set(1); // Reset to first page

            const payableCount = updatedBreakdowns.filter(b => b.status === 'PRO' && b.projected > 0).length;
            const placesToPay = payableCount > 0 ? payableCount : updatedBreakdowns.length;

            this.purseSettings.update(settings => ({
                ...settings,
                placesToPay
            }));

            this.loggingService.info('PurseComponent', `Projection loaded with ${projections.length} players`);
        } else {
            this.loggingService.warn('PurseComponent', 'No projection data available');
        }
    }

    onUpdateProjection(): void {
        const tournamentId = this.tournamentId();
        if (!tournamentId) {
            this.toastr.error('Tournament ID is required', 'Validation Error');
            return;
        }

        this.loggingService.info('PurseComponent', 'Manually updating purse projection');
        this.loadPurseProjection(tournamentId);
        this.toastr.success('Purse projection updated successfully', 'Success');
    }

    async onAssignToPlayers(): Promise<void> {
        if (!this.hasUnsavedChanges()) {
            this.toastr.warning('No changes to save', 'Info');
            return;
        }

        if (this.hasValidationErrors()) {
            this.toastr.error('Please fix validation errors before saving', 'Validation Error');
            return;
        }

        const dirtyPlayers = this.purseBreakdowns().filter(b => b.isDirty);
        const totalAssigned = dirtyPlayers.reduce((sum, b) => sum + b.assigned, 0);
        const totalProjected = dirtyPlayers.reduce((sum, b) => sum + b.projected, 0);
        const variance = totalAssigned - totalProjected;

        const totalsWarning = this.purseTotalsWarning();
        if (totalsWarning) {
            this.toastr.warning(totalsWarning, 'Purse Warning');
        }

        // Count warnings (assignments with 10-15% variance)
        const warningCount = dirtyPlayers.filter(p => {
            const variancePercent = Math.abs((p.assigned - p.projected) / p.projected) * 100;
            return variancePercent > 10 && variancePercent <= 15;
        }).length;

        // Build summary for confirmation dialog
        const summary: PurseAssignmentSummary = {
            playerCount: dirtyPlayers.length,
            totalAssigned,
            totalProjected,
            variance,
            warningCount,
            assignments: dirtyPlayers.map(p => ({
                playerName: p.playerName,
                assigned: p.assigned,
                projected: p.projected,
                variancePercent: Math.abs((p.assigned - p.projected) / p.projected) * 100
            }))
        };

        this.loggingService.info('PurseComponent', `Showing confirmation for ${dirtyPlayers.length} assignments`);

        // Show confirmation dialog
        const confirmed = await this.modalService.open(
            PurseAssignmentConfirmationDialogComponent,
            { summary }
        );

        if (confirmed) {
            this.savePlayerAssignments(dirtyPlayers);
        } else {
            this.loggingService.info('PurseComponent', 'User cancelled assignment save');
        }
    }

    private savePlayerAssignments(players: PurseBreakdown[]): void {
        const tournamentId = this.tournamentId();
        if (!tournamentId) {
            this.toastr.error('Tournament ID is required', 'Validation Error');
            return;
        }

        this.isSaving.set(true);

        const assignments: ModulesPursesApplicationPurseBreakdownsSavePlayerPurseAssignmentsPlayerPurseAssignmentDto[] = players.map(p => ({
            playerId: p.tournamentPlayerId,
            assignedPayout: p.assigned,
            isAmateur: p.status === 'AM',
            projectedPayout: p.projected
        }));

        const payload: ModulesPursesApplicationPurseBreakdownsSavePlayerPurseAssignmentsSavePlayerPurseAssignmentsRequest = {
            tournamentId,
            body: {
                assignments
            }
        };

        this.pursesService.purseBreakdownsSavePlayerAssignments(
            tournamentId,
            payload)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.isSaving.set(false))
            )
            .subscribe({
                next: () => {
                    this.markAssignmentsSaved(players);
                    this.toastr.success(`Successfully saved ${players.length} assignment(s)`, 'Success');
                    this.loggingService.info('PurseComponent', 'Assignments saved successfully');
                },
                error: error => {
                    this.loggingService.error('PurseComponent', 'Error saving assignments:', error);
                    this.toastr.error(
                        error?.error?.message || 'Failed to save player assignments',
                        'Save Error'
                    );
                }
            });
    }

    onSaveIndividualAssignment(breakdown: PurseBreakdown): void {
        const tournamentId = this.tournamentId();
        if (!tournamentId) {
            this.toastr.error('Tournament ID is required', 'Validation Error');
            return;
        }

        if (!breakdown.isDirty) {
            this.toastr.warning('No changes to save', 'Info');
            return;
        }

        if (breakdown.validationError) {
            this.toastr.error(breakdown.validationError, 'Validation Error');
            return;
        }

        this.loggingService.info('PurseComponent', `Saving individual assignment for player ${breakdown.playerId}`);

        const assignments: ModulesPursesApplicationPurseBreakdownsSavePlayerPurseAssignmentsPlayerPurseAssignmentDto[] = [{
            playerId: breakdown.tournamentPlayerId,
            assignedPayout: breakdown.assigned,
            isAmateur: breakdown.status === 'AM',
            projectedPayout: breakdown.projected
        }];

        const payload: ModulesPursesApplicationPurseBreakdownsSavePlayerPurseAssignmentsSavePlayerPurseAssignmentsRequest = {
            tournamentId,
            body: {
                assignments
            }
        };

        this.pursesService.purseBreakdownsSavePlayerAssignments(
            tournamentId,
            payload)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.isSaving.set(false))
            )
            .subscribe({
                next: () => {
                    this.markAssignmentsSaved([breakdown]);
                    this.toastr.success(`Successfully saved assignment for ${breakdown.playerName}`, 'Success');
                    this.loggingService.info('PurseComponent', 'Assignment saved successfully');
                },
                error: error => {
                    this.loggingService.error('PurseComponent', 'Error saving assignment:', error);
                    this.toastr.error(
                        error?.error?.message || 'Failed to save player assignment',
                        'Save Error'
                    );
                }
            });
    }

    onAssignmentChange(breakdown: PurseBreakdown, newValue: string): void {
        const sanitizedValue = newValue.replace(/[$,\s]/g, '').trim();
        const previousAssigned = breakdown.assigned;

        breakdown.isDirty = true;

        let parsedValue = 0;
        if (sanitizedValue !== '') {
            parsedValue = Number(sanitizedValue);
            if (!Number.isFinite(parsedValue)) {
                breakdown.assigned = previousAssigned;
                breakdown.validationError = 'Amount must be numeric';
                this.purseBreakdowns.update(b => [...b]);
                return;
            }
        }

        breakdown.assigned = parsedValue;

        breakdown.validationError = this.validateAssignment(breakdown);

        this.purseBreakdowns.update(b => [...b]);
    }

    private markAssignmentsSaved(players: PurseBreakdown[]): void {
        const updates = new Map(players.map(p => [p.playerId, p]));

        this.purseBreakdowns.update(breakdowns =>
            breakdowns.map(item => {
                const updated = updates.get(item.playerId);
                if (!updated) {
                    return item;
                }

                return {
                    ...item,
                    assigned: updated.assigned,
                    isDirty: false,
                    isEditing: false,
                    validationError: null
                };
            })
        );
    }

    onAssignmentBlur(breakdown: PurseBreakdown): void {
        // Re-format the display value when user leaves the field
        this.purseBreakdowns.update(b => [...b]);
    }

    validateAssignment(breakdown: PurseBreakdown): string | null {
        if (!Number.isFinite(breakdown.assigned)) {
            return 'Amount must be numeric';
        }

        if (breakdown.assigned < 0) {
            return 'Amount cannot be negative';
        }

        if (breakdown.status === 'AM' && breakdown.assigned !== 0) {
            return 'Amateur players cannot receive prize money';
        }

        const variance = Math.abs(breakdown.assigned - breakdown.projected);
        const variancePercent = (variance / breakdown.projected) * 100;
        const threshold = breakdown.projected * 0.15; // 15% variance threshold

        if (variance > threshold) {
            return `Exceeds 15% variance (${variancePercent.toFixed(1)}%)`;
        }

        return null;
    }

    getVarianceClass(breakdown: PurseBreakdown): string {
        if (!breakdown.assigned || breakdown.status === 'AM') {
            return '';
        }

        const variance = Math.abs(breakdown.assigned - breakdown.projected);
        const threshold = breakdown.projected * 0.15;

        if (variance > threshold) {
            return 'has-error';
        } else if (variance > breakdown.projected * 0.10) {
            return 'has-warning';
        }

        return '';
    }

    onPageChange(page: number): void {
        if (page >= 1 && page <= this.totalPages()) {
            this.currentPage.set(page);
        }
    }

    onSaveDraft(): void {
        const validationError = this.validatePurseSettings();
        if (validationError) {
            this.toastr.error(validationError, 'Validation Error');
            return;
        }

        const tournamentId = this.tournamentId();
        if (!tournamentId) {
            this.toastr.error('Tournament ID is required', 'Validation Error');
            return;
        }

        this.isSaving.set(true);
        this.loggingService.info('PurseComponent', 'Saving purse draft');

        const settings = this.purseSettings();
        const payload: { tournamentId: string; totalPurse: number; adminFeePercentage: number } = {
            tournamentId: tournamentId,
            totalPurse: settings.totalPurse,
            adminFeePercentage: settings.adminFee / 100
        };

        const existingId = this.purseBreakdownId();
        const request$ = existingId
            ? this.pursesService.purseBreakdownsPurseBreakdownIdPut(existingId, payload)
            : this.pursesService.purseBreakdownsPost(payload);

        request$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError(error => {
                    this.loggingService.error('PurseComponent', 'Error saving purse:', error);
                    this.toastr.error(error?.error?.message || 'Failed to save purse breakdown', 'Error');
                    return of(null);
                }),
                finalize(() => this.isSaving.set(false))
            )
            .subscribe(result => {
                if (result) {
                    if (!existingId) {
                        this.purseBreakdownId.set((result as any).id || (result as any).purseBreakdownId);
                    }
                    this.toastr.success(
                        existingId ? 'Purse breakdown updated successfully' : 'Purse breakdown created successfully',
                        'Success'
                    );
                    this.loggingService.info('PurseComponent', 'Purse saved successfully');
                }
            });
    }

    private syncPurseWithTournament(purseBreakdownId: string, tournamentPurse: number, adminFeePercentage: number): void {
        const payload: { tournamentId: string; totalPurse: number; adminFeePercentage: number } = {
            tournamentId: this.tournamentId()!,
            totalPurse: tournamentPurse,
            adminFeePercentage: adminFeePercentage
        };

        this.pursesService.purseBreakdownsPurseBreakdownIdPut(purseBreakdownId, payload)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError(error => {
                    this.loggingService.error('PurseComponent', 'Error syncing purse:', error);
                    // Don't show error to user - this is background sync
                    return of(null);
                })
            )
            .subscribe(result => {
                if (result) {
                    this.loggingService.info('PurseComponent', 'Purse breakdown synced with tournament purse');
                }
            });
    }

    validatePurseSettings(): string | null {
        const settings = this.purseSettings();

        if (settings.totalPurse <= 0) {
            return 'Total purse must be greater than 0';
        }

        if (settings.adminFee < 0 || settings.adminFee > 100) {
            return 'Admin fee must be between 0 and 100';
        }

        if (settings.firstPlacePercentage < 0 || settings.firstPlacePercentage > 100) {
            return 'First place percentage must be between 0 and 100';
        }

        if (settings.placesToPay <= 0) {
            return 'Places to pay must be greater than 0';
        }

        return null;
    }

    onFinalize(): void {
        this.loggingService.info('PurseComponent', 'Finalizing and publishing purse');
        // TODO: Implement finalize and publish
    }

    getPositionClass(position: string): string {
        if (position.includes('1')) {
            return 'label-primary';
        } else if (position.includes('3')) {
            return 'label-info';
        } else {
            return 'label-default';
        }
    }

    formatCurrency(value: number): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    }

    formatCurrencyDetailed(value: number): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    }
}
